import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  IconButton,
  Tooltip,
  Skeleton,
  Fade,
  Switch,
  FormControlLabel,
  InputAdornment
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { motion } from 'framer-motion';
import EventIcon from '@mui/icons-material/Event';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ViewDayIcon from '@mui/icons-material/ViewDay';
import View3DayIcon from '@mui/icons-material/ViewWeek';
import ViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import RefreshIcon from '@mui/icons-material/Refresh';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { format } from 'date-fns';
import ChatIcon from '@mui/icons-material/Chat';
import SearchIcon from '@mui/icons-material/Search';
import ScheduleIcon from '@mui/icons-material/Schedule';

// Import FullCalendar and its plugins
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

const Settings = () => {
  const { user } = useAuth();
  const { mode, toggleColorMode } = useTheme();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [events, setEvents] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [calendarView, setCalendarView] = useState('timeGridDay');
  const [availableCalendars, setAvailableCalendars] = useState([]);
  const [selectedCalendars, setSelectedCalendars] = useState([]);
  const calendarRef = useRef(null);
  const [availabilityQuery, setAvailabilityQuery] = useState('');
  const [availabilityResult, setAvailabilityResult] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to change password');
      }

      setSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAPISubmit = async (e) => {
    e.preventDefault();
    // Add API keys submission logic here
  };

  useEffect(() => {
    // Check if calendar is connected
    fetchEvents();
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin === window.location.origin && event.data.type === 'GOOGLE_AUTH_CODE') {
        handleAuthCallback(event.data.code);
        setOpenDialog(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const fetchEvents = async () => {
    setLoadingEvents(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/calendar/events`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Format events for FullCalendar
        const formattedEvents = data.events.map(event => ({
          id: event.id,
          title: event.summary,
          start: event.start.dateTime || event.start.date,
          end: event.end.dateTime || event.end.date,
          allDay: !event.start.dateTime,
          location: event.location,
          description: event.description,
          attendees: event.attendees,
          originalEvent: event,
          backgroundColor: event.calendarColor || '#3788d8',
          borderColor: event.calendarColor || '#3788d8',
          extendedProps: {
            calendarId: event.calendarId || 'default',
            calendarName: event.calendarName || 'Default',
            location: event.location,
            description: event.description,
            attendees: event.attendees
          }
        }));
        
        setEvents(formattedEvents);
        setCalendarConnected(true);
        
        // Also set available calendars and initialize selected calendars
        if (data.calendars && data.calendars.length > 0) {
          setAvailableCalendars(data.calendars);
          
          // Initialize with all calendars selected
          if (selectedCalendars.length === 0) {
            setSelectedCalendars(data.calendars.map(cal => cal.id));
          }
        }
      } else if (response.status === 400) {
        // Calendar not connected
        setCalendarConnected(false);
        setEvents([]);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch calendar events');
      }
    } catch (error) {
      console.error('Error fetching calendar events:', error);
      setError('Error connecting to server');
    } finally {
      setLoadingEvents(false);
    }
  };

  const handleConnectCalendar = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/calendar/auth/google`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Open Google authorization in a new window
        window.open(data.authorization_url, '_blank', 'width=800,height=600');
        setOpenDialog(true);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to initiate Google authorization');
      }
    } catch (error) {
      console.error('Error connecting to Google Calendar:', error);
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  const handleAuthCallback = async (code) => {
    try {
      setLoading(true);
      console.log("Received auth code, sending to backend...");
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/calendar/auth/google/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ code })
      });
      
      // Check if response is JSON
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error("Received non-JSON response:", text);
        throw new Error("Received invalid response from server");
      }
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to connect Google Calendar');
      }
      
      // Successfully connected
      setCalendarConnected(true);
      fetchEvents();
      setError(null);
    } catch (err) {
      console.error("Error in handleAuthCallback:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnectCalendar = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/calendar/disconnect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      if (response.ok) {
        setCalendarConnected(false);
        setEvents([]);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to disconnect Google Calendar');
      }
    } catch (error) {
      console.error('Error disconnecting Google Calendar:', error);
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    const code = new FormData(e.target).get('code');
    if (code) {
      handleAuthCallback(code);
    }
  };

  const handleEventClick = (info) => {
    setSelectedEvent(info.event);
    setEventDialogOpen(true);
  };

  const handleCloseEventDialog = () => {
    setEventDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setCalendarView(newView);
      // Refresh calendar data when changing views
      fetchEvents();
    }
  };

  const refreshCalendar = () => {
    fetchEvents();
  };

  const toggleCalendarVisibility = (calendarId) => {
    console.log("Toggling calendar:", calendarId);
    console.log("Current selected calendars:", selectedCalendars);
    
    if (selectedCalendars.includes(calendarId)) {
      setSelectedCalendars(selectedCalendars.filter(id => id !== calendarId));
    } else {
      setSelectedCalendars([...selectedCalendars, calendarId]);
    }
  };

  const checkAvailabilityFromText = async (query) => {
    if (!query.trim()) {
      setAvailabilityError('Please enter a question about your availability');
      return;
    }
    
    setCheckingAvailability(true);
    setAvailabilityError('');
    setAvailabilityResult(null);
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${apiUrl}/api/calendar/check-availability-from-text`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : undefined,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          query: query 
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvailabilityResult(data);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to check availability');
      }
    } catch (error) {
      console.error('Error checking availability:', error);
      setAvailabilityError(error.message);
    } finally {
      setCheckingAvailability(false);
    }
  };
  
  const handleAvailabilityQueryChange = (e) => {
    setAvailabilityQuery(e.target.value);
  };
  
  const handleAvailabilityQuerySubmit = (e) => {
    e.preventDefault();
    checkAvailabilityFromText(availabilityQuery);
  };
  
  const formatAvailabilityResult = (result) => {
    if (!result) return null;
    
    if (result.available) {
      return (
        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="body1" fontWeight="medium">
            Yes, you're available during this time!
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {result.message || 'The requested time slot is free on your calendar.'}
          </Typography>
        </Alert>
      );
    } else {
      return (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body1" fontWeight="medium">
            You have conflicts during this time.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {result.message || 'There are events scheduled during this time.'}
          </Typography>
          {result.conflicts && result.conflicts.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" fontWeight="medium">Conflicts:</Typography>
              <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1, mt: 0.5 }}>
                {result.conflicts.map((conflict, index) => (
                  <ListItem key={index} divider={index < result.conflicts.length - 1}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <EventIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={conflict.summary} 
                      secondary={`${new Date(conflict.start).toLocaleTimeString()} - ${new Date(conflict.end).toLocaleTimeString()}`}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Alert>
      );
    }
  };

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      sx={{ p: 3 }}
    >
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Profile Settings
              </Typography>
              <Box component="form" sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Name"
                  defaultValue={user?.name}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Email"
                  defaultValue={user?.email}
                  margin="normal"
                  disabled
                />
                <Button 
                  variant="contained" 
                  type="submit"
                  sx={{ mt: 2 }}
                >
                  Save Changes
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Theme Settings Card */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Appearance
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {mode === 'dark' ? (
                    <DarkModeIcon sx={{ mr: 1, color: 'primary.main' }} />
                  ) : (
                    <LightModeIcon sx={{ mr: 1, color: 'warning.main' }} />
                  )}
                  <Typography>
                    {mode === 'dark' ? 'Dark Mode' : 'Light Mode'}
                  </Typography>
                </Box>
                <Switch
                  checked={mode === 'dark'}
                  onChange={toggleColorMode}
                  color="primary"
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Your theme preference will be saved and applied across all your sessions.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                API Settings
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Configure your API keys and integrations
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box component="form" onSubmit={handleAPISubmit}>
                <TextField
                  fullWidth
                  label="Twilio API Key"
                  type="password"
                  margin="normal"
                  autoComplete="new-password"
                />
                <TextField
                  fullWidth
                  label="OpenAI API Key"
                  type="password"
                  margin="normal"
                  autoComplete="new-password"
                />
                <Button 
                  variant="contained" 
                  type="submit"
                  sx={{ mt: 2 }}
                >
                  Save API Keys
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>Change Password</Typography>
              <form onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
                  <TextField
                    label="Current Password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    fullWidth
                    autoComplete="current-password"
                  />
                  <TextField
                    label="New Password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    fullWidth
                    autoComplete="new-password"
                    helperText="Password must be at least 8 characters long"
                  />
                  <TextField
                    label="Confirm New Password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    fullWidth
                    autoComplete="new-password"
                  />
                  {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {error}
                    </Alert>
                  )}
                  {success && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      {success}
                    </Alert>
                  )}
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isLoading}
                    sx={{ mt: 2 }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Change Password'
                    )}
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h2">
                  Calendar Integration
                </Typography>
                {calendarConnected ? (
                  <Button 
                    variant="outlined" 
                    color="error" 
                    startIcon={<LinkOffIcon />}
                    onClick={handleDisconnectCalendar}
                    disabled={loading}
                  >
                    Disconnect
                  </Button>
                ) : (
                  <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<LinkIcon />}
                    onClick={handleConnectCalendar}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Connect Google Calendar'}
                  </Button>
                )}
              </Box>
              
              {calendarConnected ? (
                <Box>
                  {loadingEvents ? (
                    <Fade in={loadingEvents}>
                      <Box sx={{ p: 2 }}>
                        <Skeleton variant="rectangular" height={60} sx={{ mb: 2, borderRadius: 1 }} />
                        <Skeleton variant="rectangular" height={500} sx={{ borderRadius: 1 }} />
                      </Box>
                    </Fade>
                  ) : events.length > 0 ? (
                    <Box>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        mb: 2 
                      }}>
                        <ToggleButtonGroup
                          value={calendarView}
                          exclusive
                          onChange={handleViewChange}
                          aria-label="calendar view"
                          size="small"
                        >
                          <ToggleButton value="timeGridDay" aria-label="day view">
                            <Tooltip title="Day View">
                              <ViewDayIcon />
                            </Tooltip>
                          </ToggleButton>
                          <ToggleButton value="timeGridThreeDay" aria-label="3 day view">
                            <Tooltip title="3 Day View">
                              <View3DayIcon />
                            </Tooltip>
                          </ToggleButton>
                          <ToggleButton value="timeGridWeek" aria-label="week view">
                            <Tooltip title="Week View">
                              <ViewWeekIcon />
                            </Tooltip>
                          </ToggleButton>
                        </ToggleButtonGroup>
                        
                        <Tooltip title="Refresh Calendar">
                          <IconButton onClick={refreshCalendar} disabled={loadingEvents}>
                            <RefreshIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      
                      <Paper 
                        elevation={3} 
                        sx={{ 
                          p: 2, 
                          height: 600, 
                          borderRadius: 2,
                          overflow: 'hidden',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: 6
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', mb: 2 }}>
                          {availableCalendars.map(calendar => (
                            <Chip
                              key={calendar.id}
                              label={calendar.summary}
                              sx={{
                                mr: 1,
                                backgroundColor: selectedCalendars.includes(calendar.id) ? calendar.backgroundColor : 'transparent',
                                color: selectedCalendars.includes(calendar.id) ? '#fff' : 'text.primary',
                                borderColor: calendar.backgroundColor,
                                border: '1px solid'
                              }}
                              onClick={() => toggleCalendarVisibility(calendar.id)}
                              variant={selectedCalendars.includes(calendar.id) ? "filled" : "outlined"}
                            />
                          ))}
                        </Box>
                        
                        <FullCalendar
                          ref={calendarRef}
                          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                          initialView={calendarView}
                          headerToolbar={{
                            left: 'prev,next today',
                            center: 'title',
                            right: ''
                          }}
                          views={{
                            timeGridThreeDay: {
                              type: 'timeGrid',
                              duration: { days: 3 }
                            }
                          }}
                          events={events.filter(event => 
                            selectedCalendars.length === 0 || 
                            !event.extendedProps || 
                            !event.extendedProps.calendarId || 
                            selectedCalendars.includes(event.extendedProps.calendarId)
                          )}
                          eventClick={handleEventClick}
                          height="100%"
                          slotMinTime="06:00:00"
                          slotMaxTime="22:00:00"
                          allDaySlot={true}
                          nowIndicator={true}
                          eventTimeFormat={{
                            hour: '2-digit',
                            minute: '2-digit',
                            meridiem: false
                          }}
                          themeSystem="standard"
                          eventDisplay="block"
                          eventBackgroundColor="#3788d8"
                          eventBorderColor="#3788d8"
                          eventTextColor="#fff"
                          slotLabelFormat={{
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                          }}
                          dayHeaderFormat={{ weekday: 'short', day: 'numeric' }}
                          firstDay={1}
                          slotEventOverlap={false}
                          stickyHeaderDates={true}
                          dayMaxEvents={true}
                          eventMaxStack={3}
                          eventContent={(arg) => (
                            <Box sx={{ 
                              p: 0.5, 
                              fontSize: '0.85rem', 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              animation: 'fadeIn 0.3s ease',
                              '@keyframes fadeIn': {
                                from: { opacity: 0 },
                                to: { opacity: 1 }
                              }
                            }}>
                              <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>
                                {arg.timeText}
                              </Typography>
                              <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {arg.event.title}
                              </Typography>
                            </Box>
                          )}
                        />
                      </Paper>
                    </Box>
                  ) : (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      py: 4,
                      bgcolor: 'rgba(0, 0, 0, 0.02)',
                      borderRadius: 1
                    }}>
                      <CalendarMonthIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="body1" color="text.secondary">
                        No upcoming events found
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  py: 4,
                  bgcolor: 'rgba(0, 0, 0, 0.02)',
                  borderRadius: 1
                }}>
                  <CalendarMonthIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    Connect your Google Calendar to see your upcoming events
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 400, mb: 2 }}>
                    This integration allows Receptify to view your calendar events to help manage your schedule.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ChatIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2">
                  Calendar Availability Checker
                </Typography>
              </Box>
              
              {calendarConnected ? (
                <Box>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Ask a question about your schedule to check availability.
                    Examples: "Am I available tomorrow at 2pm?", "Do I have any meetings on Friday?", "When am I free next week?"
                  </Typography>
                  
                  <Box component="form" onSubmit={handleAvailabilityQuerySubmit}>
                    <TextField
                      fullWidth
                      label="Ask about your availability"
                      variant="outlined"
                      value={availabilityQuery}
                      onChange={handleAvailabilityQueryChange}
                      placeholder="e.g., Am I free tomorrow at 3pm?"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ScheduleIcon color="primary" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton 
                              edge="end" 
                              type="submit"
                              disabled={checkingAvailability || !availabilityQuery.trim()}
                            >
                              {checkingAvailability ? (
                                <CircularProgress size={24} />
                              ) : (
                                <SearchIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      disabled={checkingAvailability}
                    />
                  </Box>
                  
                  {availabilityError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {availabilityError}
                    </Alert>
                  )}
                  
                  {formatAvailabilityResult(availabilityResult)}
                  
                </Box>
              ) : (
                <Box sx={{ 
                  p: 3, 
                  bgcolor: 'rgba(0, 0, 0, 0.02)', 
                  borderRadius: 1, 
                  textAlign: 'center' 
                }}>
                  <Typography variant="body1" color="textSecondary" gutterBottom>
                    Connect your Google Calendar to use the availability checker.
                  </Typography>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<LinkIcon />}
                    onClick={handleConnectCalendar}
                    disabled={loading}
                    sx={{ mt: 1 }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Connect Google Calendar'}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Auth callback dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Complete Google Calendar Authorization</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            After authorizing in Google, you'll receive an authorization code. 
            Please copy and paste it below:
          </Typography>
          <form id="auth-form" onSubmit={handleCodeSubmit}>
            <input 
              type="text" 
              name="code" 
              placeholder="Paste authorization code here"
              style={{ 
                width: '100%', 
                padding: '10px', 
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            type="submit" 
            form="auth-form" 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Connect'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Event details dialog */}
      <Dialog open={eventDialogOpen} onClose={handleCloseEventDialog} maxWidth="sm" fullWidth>
        {selectedEvent && (
          <>
            <DialogTitle>
              {selectedEvent.title || 'Untitled Event'}
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold">Time</Typography>
                  <Typography variant="body1">
                    {selectedEvent.allDay ? (
                      format(new Date(selectedEvent.start), 'PPP')
                    ) : (
                      <>
                        {format(new Date(selectedEvent.start), 'PPP p')} - {format(new Date(selectedEvent.end), 'p')}
                      </>
                    )}
                  </Typography>
                </Grid>
                
                {selectedEvent.extendedProps.location && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight="bold">Location</Typography>
                    <Typography variant="body1">{selectedEvent.extendedProps.location}</Typography>
                  </Grid>
                )}
                
                {selectedEvent.extendedProps.description && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight="bold">Description</Typography>
                    <Typography variant="body1">{selectedEvent.extendedProps.description}</Typography>
                  </Grid>
                )}
                
                {selectedEvent.extendedProps.attendees && selectedEvent.extendedProps.attendees.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight="bold">Attendees</Typography>
                    <List dense>
                      {selectedEvent.extendedProps.attendees.map((attendee, index) => (
                        <ListItem key={index}>
                          <ListItemText 
                            primary={attendee.displayName || attendee.email} 
                            secondary={attendee.responseStatus === 'accepted' ? 'Accepted' : 
                                      attendee.responseStatus === 'declined' ? 'Declined' : 
                                      attendee.responseStatus === 'tentative' ? 'Maybe' : 'No response'}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseEventDialog}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Settings; 